import { useForm, Controller } from "react-hook-form";
import React, { useContext, useState } from "react";
import { showToast } from "../../../Forms/Dialogs/Toast/Toast";
import "./LaunchAction.scss";
import {
  TextButton,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import TextareaAutoSize from "../../../Forms/Controls/TextareaAutoSize/TextareaAutoSize";
import {
  CNC_LAUNCH_ACTION,
  type launchActionMutationVariablesType,
  type launchActionMutationOptionsType,
  type launchActionDataType,
} from "../ActionsAPI";

interface LaunchActionProps {
  actionId: string;
  actionDisplayName: string;
  actionParams: string;
  accountName: string;
  accountUuid: string;
}

interface LaunchActionFormData {
  overrideParamsJSON?: string;
}

// Shape of each entry in the parsed `launched_tasks` JSON string. Only the
// count is read now — an empty array means the action was already running.
type LaunchedTask = { task_name?: string };

const LaunchAction: React.FC<LaunchActionProps> = ({
  actionId,
  actionDisplayName,
  actionParams,
  accountName,
  accountUuid,
}) => {
  const methods = useForm<LaunchActionFormData>({ mode: "all" });
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = methods;

  const [launchAction] =
    useMutationWrapper<launchActionDataType>(CNC_LAUNCH_ACTION);

  const { closeModal } = useContext(ModalContext);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const onSubmit = (formData: LaunchActionFormData) => {
    setIsSubmittingForm(true);

    let vars: launchActionMutationVariablesType = {
      account_uuid: accountUuid,
      action_id: actionId,
      // Progress notifications were delivered over the WebSocket, which has
      // been removed (WAK-38656). Always opt out so the flow jobs skip
      // publishing entirely.
      notify_progress: false,
    };

    if (!!formData.overrideParamsJSON) {
      vars = { ...vars, params_override: formData.overrideParamsJSON };
    }

    const launchActionOptions: launchActionMutationOptionsType = {
      variables: vars,
    };

    launchAction(launchActionOptions)
      .then((res) => {
        setIsSubmittingForm(false);

        if (res?.data?.cnc_action_launcher?.launched_tasks) {
          let launchedTasks: LaunchedTask[] = [];
          try {
            launchedTasks = JSON.parse(
              res?.data?.cnc_action_launcher?.launched_tasks
            );
          } catch {
            // A non-JSON payload should not surface a parser error to the
            // user — the action was accepted either way.
            showToast({ message: "Action launched", description: accountName });
            closeModal();
            return;
          }

          if (launchedTasks?.length) {
            showToast({
              message: `Launched ${launchedTasks.length} ${
                launchedTasks.length === 1 ? "task" : "tasks"
              }`,
              description: accountName,
            });
          } else {
            showToast({ message: "Action is already running" });
          }
        }

        closeModal();
      })
      .catch((err: Error) => {
        setIsSubmittingForm(false);
        showErrorDialog({ message: err.message });
      });
  };

  return (
    <div className={"action-launcher-wrapper"}>
      <div className={"action-details"}>
        You are about to run the following action, you may override it's
        parameters:
        <h5> {actionDisplayName} </h5>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name={"overrideParamsJSON"}
          render={({ field: { value, onChange, onBlur } }) => {
            return (
              <TextareaAutoSize
                wrapperClassName={`override-params-textarea`}
                data-testid={"overrideParamsJSON"}
                name={"overrideParamsJSON"}
                value={value}
                onBlur={onBlur}
                labelTop
                onChange={(data) => onChange(data)}
                label={"Parameters (JSON formatted)"}
                minRows={3}
              />
            );
          }}
          control={control}
          defaultValue={actionParams}
        />
        <ModalButtonsContainer>
          <TextButton
            type="button"
            data-testid={"launch-action-cancel"}
            label={"Cancel"}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              closeModal();
            }}
          />
          <TextButton
            type="submit"
            label={"Launch Action"}
            data-testid={"launch-action"}
            buttonStyle={"primary"}
            rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
            isSubmitting={isSubmittingForm}
            disabled={!isValid || isSubmittingForm}
          />
        </ModalButtonsContainer>
      </form>
    </div>
  );
};
export default LaunchAction;
